import { forwardRef, useImperativeHandle, useRef } from 'react';
import { observer } from 'mobx-react-lite';
import { InputProps } from '../types';
import { loader } from 'assets/icons';
import { Container } from '../../common.styled';
import { InputContainer, ActionIcon } from '../inputs.styled';
import { CustomizedInput, Icon } from './base-input.styled';
import { Message } from '../../components';

/* Каждый из пропсов необязателен.
 * Помимо перечисленных ниже пропсов, также принимает любые InputHTMLAttributes.
 * state позволяет передавать состояние инпута, цвет:
 * default - серый, голубой фокус; success - зеленый; warning - желтый; error - красный.
 * width позволяет задать ширину инпута:
 * inherit - динамическая, по ширине родителя; s/m/l - статические, 320/520/720px.
 * message передает сообщение под инпутом, можно передать различные сообщения для каждого state
 * в icon передается ссылка на декоративную картинку слева
 * в actionIcon передается ссылка на картинку и функция по клику эту картинку
 * */
export const BaseInput = observer(
  forwardRef<HTMLInputElement, InputProps>(
    (
      {
        state = 'default',
        width = 's',
        message,
        icon,
        actionIcon,
        pending,
        ...inputProps
      },
      forwardedRef
    ) => {
      const inputRef = useRef<HTMLInputElement>(null);
      const shouldShowActionIcon = actionIcon ? actionIcon.show ?? true : false;

      useImperativeHandle(forwardedRef, () => inputRef.current!);

      const focusInput = () => {
        !inputProps.disabled && inputRef.current && inputRef.current.focus();
      };

      const upgradedIconAction = () => {
        actionIcon && actionIcon.action();
        focusInput();
      };

      return (
        <Container
          $state={state}
          $width={width}
          $disabled={inputProps.disabled || false}
          style={inputProps.style}
        >
          <InputContainer>
            {!!icon && <Icon src={icon} onClick={focusInput} />}
            <CustomizedInput
              ref={inputRef}
              $icon={!!icon}
              $actionIcon={shouldShowActionIcon}
              {...inputProps}
            />

            {!!pending && <ActionIcon src={loader} $isPending />}

            {actionIcon && shouldShowActionIcon && (
              <ActionIcon
                src={actionIcon.pending ? loader : actionIcon.src}
                onClick={upgradedIconAction}
                $isDisabled={inputProps.disabled}
                $isPending={actionIcon.pending}
              />
            )}
          </InputContainer>
          {!!message?.[state] && <Message state={state} message={message} />}
        </Container>
      );
    }
  )
);
